export const SUPPORTED_CURRENCIES = ["USD", "INR"] as const;

export type SupportedCurrency = (typeof SUPPORTED_CURRENCIES)[number];

type ExchangeRateResponse = {
  base?: string;
  date?: string;
  rates?: Record<string, number>;
};

type ExchangeRateApiResponse = {
  result?: string;
  time_last_update_utc?: string;
  rates?: Record<string, number>;
};

type ExchangeRateResult = {
  date: string;
  rate: number;
  source: string;
};

const FRANKFURTER_API = "https://api.frankfurter.dev/v1/latest";
const EXCHANGE_RATE_API = "https://open.er-api.com/v6/latest";
const CACHE_TTL_MS = 30 * 60 * 1000;
const REQUEST_TIMEOUT_MS = 8000;

const exchangeRateCache = new Map<
  string,
  {
    expiresAt: number;
    value: ExchangeRateResult;
  }
>();

const currencyLocaleMap: Record<SupportedCurrency, string> = {
  USD: "en-US",
  INR: "en-IN",
};

export function normalizeCurrencyCode(
  currency?: string | null,
): SupportedCurrency {
  const normalizedCurrency = currency?.toUpperCase();

  if (
    normalizedCurrency &&
    SUPPORTED_CURRENCIES.includes(normalizedCurrency as SupportedCurrency)
  ) {
    return normalizedCurrency as SupportedCurrency;
  }

  return "USD";
}

export function formatCurrencyAmount(
  amount: number,
  currency: SupportedCurrency,
) {
  return new Intl.NumberFormat(currencyLocaleMap[currency], {
    currency,
    style: "currency",
    maximumFractionDigits: 2,
  }).format(amount);
}

export async function getExchangeRate(
  fromCurrency: SupportedCurrency,
  toCurrency: SupportedCurrency,
) {
  if (fromCurrency === toCurrency) {
    return {
      date: "",
      rate: 1,
      source: "same-currency",
    };
  }

  const cacheKey = `${fromCurrency}_${toCurrency}`;
  const cachedValue = exchangeRateCache.get(cacheKey);

  if (cachedValue && cachedValue.expiresAt > Date.now()) {
    return cachedValue.value;
  }

  // Every provider is tried in turn so one flaky or browser-hostile host does
  // not cost us the conversion. ExchangeRate-API leads because it is the only
  // one of the two that answers with `Access-Control-Allow-Origin`, so a
  // browser can actually read its response; Frankfurter is CORS-blocked from
  // the page and only survives here as a server-side/last-resort attempt.
  let lastError: unknown;

  for (const fetchRate of [fetchExchangeRateApiRate, fetchFrankfurterRate]) {
    try {
      const result = await fetchRate(fromCurrency, toCurrency);

      exchangeRateCache.set(cacheKey, {
        expiresAt: Date.now() + CACHE_TTL_MS,
        value: result,
      });

      return result;
    } catch (error) {
      lastError = error;
    }
  }

  throw lastError instanceof Error
    ? lastError
    : new Error("Exchange rate is unavailable");
}

// A provider that never answers would otherwise leave the converter stuck on
// "Fetching latest rate...", so each attempt is given a hard deadline.
async function fetchWithTimeout(requestUrl: string) {
  if (typeof AbortController === "undefined") {
    return fetch(requestUrl);
  }

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

  try {
    return await fetch(requestUrl, { signal: controller.signal });
  } finally {
    clearTimeout(timeoutId);
  }
}

async function fetchExchangeRateApiRate(
  fromCurrency: SupportedCurrency,
  toCurrency: SupportedCurrency,
) {
  const response = await fetchWithTimeout(
    `${EXCHANGE_RATE_API}/${fromCurrency}`,
  );

  if (!response.ok) {
    throw new Error("Unable to fetch ExchangeRate-API rate");
  }

  const data: ExchangeRateApiResponse = await response.json();
  const rate = data?.rates?.[toCurrency];

  if (data?.result !== "success" || typeof rate !== "number") {
    throw new Error("ExchangeRate-API rate is unavailable");
  }

  return {
    date: data?.time_last_update_utc ?? "",
    rate,
    source: "ExchangeRate-API",
  };
}

async function fetchFrankfurterRate(
  fromCurrency: SupportedCurrency,
  toCurrency: SupportedCurrency,
) {
  const requestUrl = new URL(FRANKFURTER_API);
  requestUrl.searchParams.set("base", fromCurrency);
  requestUrl.searchParams.set("symbols", toCurrency);

  const response = await fetchWithTimeout(requestUrl.toString());

  if (!response.ok) {
    throw new Error("Unable to fetch latest Frankfurter exchange rate");
  }

  const data: ExchangeRateResponse = await response.json();
  const rate = data?.rates?.[toCurrency];

  if (typeof rate !== "number") {
    throw new Error("Latest Frankfurter exchange rate is unavailable");
  }

  return {
    date: data?.date ?? "",
    rate,
    source: "Frankfurter",
  };
}
